import { useState, useEffect, useRef } from 'react';
import { AdminLayout } from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { UserPlus, Users, Trash2, X, BarChart3, Shuffle, User, Mail, Phone, Target, Camera, Upload, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COLORS = {
  new_lead: '#3B82F6', contacted: '#8B5CF6', qualified: '#F59E0B',
  closed: '#22C55E', lost: '#EF4444'
};

// ============ IMAGE UPLOAD COMPONENT ============
const ImageUploader = ({ currentUrl, onUpload, size = 'md' }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl || '');

  useEffect(() => { setPreview(currentUrl || ''); }, [currentUrl]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    // Show local preview immediately
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreview(res.data.url);
      onUpload(res.data.url);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      setPreview(currentUrl || '');
    } finally {
      setUploading(false);
    }
  };

  const dim = size === 'lg' ? 'w-24 h-24' : 'w-12 h-12';
  const iconDim = size === 'lg' ? 'w-10 h-10' : 'w-6 h-6';

  return (
    <div className="relative group cursor-pointer" onClick={() => fileRef.current?.click()} data-testid="image-uploader">
      <input type="file" ref={fileRef} onChange={handleFileChange} accept="image/*" className="hidden" data-testid="image-file-input" />
      {preview ? (
        <img src={preview} alt="Profile" className={`${dim} rounded-full object-cover border-2 border-[#D4AF37]/30`} />
      ) : (
        <div className={`${dim} rounded-full bg-[#D4AF37]/10 flex items-center justify-center border-2 border-dashed border-[#D4AF37]/30`}>
          <User className={`${iconDim} text-[#D4AF37]/50`} />
        </div>
      )}
      <div className={`absolute inset-0 ${dim} rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity`}>
        {uploading ? (
          <Loader2 className="w-5 h-5 text-white animate-spin" />
        ) : (
          <Camera className="w-5 h-5 text-white" />
        )}
      </div>
    </div>
  );
};

export const AdminAgents = () => {
  const [agents, setAgents] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [assigning, setAssigning] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [agentsRes, perfRes] = await Promise.all([
        axios.get(`${API}/agents`),
        axios.get(`${API}/agents/performance`)
      ]);
      setAgents(agentsRes.data);
      setPerformance(perfRes.data);
    } catch (error) { console.error('Agents fetch error:', error); } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error('Name, email, and password are required');
      return;
    }
    try {
      await axios.post(`${API}/agents`, form);
      toast.success(`Agent ${form.name} created`);
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', phone: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create agent');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete agent ${name}? Their leads will be unassigned.`)) return;
    try {
      await axios.delete(`${API}/agents/${id}`);
      toast.success('Agent deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleRoundRobin = async () => {
    setAssigning(true);
    try {
      const res = await axios.post(`${API}/leads/assign/round-robin`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Round-robin failed');
    } finally { setAssigning(false); }
  };

  const handlePhotoUpload = async (agentId, photoUrl) => {
    try {
      await axios.put(`${API}/agents/${agentId}`, { photo_url: photoUrl });
      fetchData();
    } catch {
      toast.error('Failed to save photo');
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-agents">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
              Agent Management
            </h1>
            <p className="text-[#808080] mt-1">Manage agents, assign leads, track performance</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRoundRobin} variant="outline" className="btn-outline-gold" disabled={assigning || agents.length === 0} data-testid="round-robin-btn">
              <Shuffle className="w-4 h-4 mr-2" /> {assigning ? 'Assigning...' : 'Round-Robin Assign'}
            </Button>
            <Button onClick={() => setShowCreate(true)} className="btn-gold" data-testid="add-agent-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Add Agent
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner" /></div>
        ) : (
          <>
            {/* Agents Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="agents-grid">
              {agents.length === 0 ? (
                <div className="col-span-full dashboard-card text-center py-12">
                  <Users className="w-12 h-12 text-[#D4AF37]/30 mx-auto mb-3" />
                  <p className="text-[#808080]">No agents yet. Add your first agent to start assigning leads.</p>
                </div>
              ) : (
                agents.map(agent => {
                  const perf = performance.find(p => p.agent_id === agent.id) || {};
                  return (
                    <div key={agent.id} className="dashboard-card" data-testid={`agent-card-${agent.id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <ImageUploader
                            currentUrl={agent.photo_url}
                            onUpload={(url) => handlePhotoUpload(agent.id, url)}
                          />
                          <div>
                            <h3 className="text-[#F5F5F0] font-medium">{agent.name}</h3>
                            <p className="text-[#808080] text-xs">{agent.email}</p>
                          </div>
                        </div>
                        <button onClick={() => handleDelete(agent.id, agent.name)} className="text-red-400 hover:bg-red-400/10 p-1.5 rounded" data-testid={`delete-agent-${agent.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Performance Stats */}
                      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-[#222]">
                        <div className="text-center">
                          <p className="text-lg font-semibold text-[#F5F5F0]">{perf.total_assigned || 0}</p>
                          <p className="text-[#808080] text-[10px] uppercase">Assigned</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-semibold text-green-400">{perf.closed || 0}</p>
                          <p className="text-[#808080] text-[10px] uppercase">Closed</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-semibold text-[#D4AF37]">${((perf.total_deal_value || 0) / 1000).toFixed(0)}K</p>
                          <p className="text-[#808080] text-[10px] uppercase">Value</p>
                        </div>
                      </div>

                      {/* Stage Breakdown Mini */}
                      <div className="flex gap-1 mt-3">
                        {['new_lead', 'contacted', 'qualified', 'closed', 'lost'].map(stage => {
                          const count = perf[stage] || 0;
                          return count > 0 ? (
                            <span key={stage} className="px-1.5 py-0.5 text-[10px] rounded" style={{ backgroundColor: `${STAGE_COLORS[stage]}20`, color: STAGE_COLORS[stage] }}>
                              {stage.replace('_', ' ')}: {count}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Performance Table */}
            {performance.length > 0 && (
              <div className="dashboard-card" data-testid="performance-table">
                <h3 className="text-lg text-[#F5F5F0] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                  <BarChart3 className="w-5 h-5 inline mr-2 text-[#D4AF37]" />
                  Agent Performance
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#D4AF37]/10">
                        <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase font-medium">Agent</th>
                        <th className="text-center py-3 px-4 text-[#808080] text-xs uppercase font-medium">Assigned</th>
                        <th className="text-center py-3 px-4 text-[#808080] text-xs uppercase font-medium">New</th>
                        <th className="text-center py-3 px-4 text-[#808080] text-xs uppercase font-medium">Contacted</th>
                        <th className="text-center py-3 px-4 text-[#808080] text-xs uppercase font-medium">Qualified</th>
                        <th className="text-center py-3 px-4 text-[#808080] text-xs uppercase font-medium">Closed</th>
                        <th className="text-center py-3 px-4 text-[#808080] text-xs uppercase font-medium">Lost</th>
                        <th className="text-center py-3 px-4 text-[#808080] text-xs uppercase font-medium">Deal Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {performance.map(p => (
                        <tr key={p.agent_id} className="border-b border-[#1A1A1A]">
                          <td className="py-3 px-4 text-[#F5F5F0] text-sm font-medium">{p.agent_name}</td>
                          <td className="py-3 px-4 text-center text-[#F5F5F0]">{p.total_assigned}</td>
                          <td className="py-3 px-4 text-center" style={{ color: STAGE_COLORS.new_lead }}>{p.new_lead}</td>
                          <td className="py-3 px-4 text-center" style={{ color: STAGE_COLORS.contacted }}>{p.contacted}</td>
                          <td className="py-3 px-4 text-center" style={{ color: STAGE_COLORS.qualified }}>{p.qualified}</td>
                          <td className="py-3 px-4 text-center" style={{ color: STAGE_COLORS.closed }}>{p.closed}</td>
                          <td className="py-3 px-4 text-center" style={{ color: STAGE_COLORS.lost }}>{p.lost}</td>
                          <td className="py-3 px-4 text-center text-[#D4AF37] font-semibold">${p.total_deal_value.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Create Agent Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="create-agent-modal">
            <div className="bg-[#111111] p-6 rounded-xl max-w-md w-full mx-4 border border-[#D4AF37]/20">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>Add Agent</h3>
                <button onClick={() => setShowCreate(false)} className="text-[#808080] hover:text-[#F5F5F0]">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Full Name</label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Agent name" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="agent-name-input" />
                </div>
                <div>
                  <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Email</label>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="agent@email.com" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="agent-email-input" />
                </div>
                <div>
                  <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Password</label>
                  <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Login password" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="agent-password-input" />
                </div>
                <div>
                  <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Phone (optional)</label>
                  <Input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Phone number" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="agent-phone-input" />
                </div>
                <Button onClick={handleCreate} className="btn-gold w-full" data-testid="save-agent-btn">
                  Create Agent Account
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
